<template>
  <BInputGroup>
    <BFormInput :id="inputId" v-model="code" required :state="codeState" :class="inputVariantClass" />
    <template #append>
      <BButton type="button" :variant="buttonVariant" :disabled="checkInProgress" @click="check">
        <BSpinner v-if="checkInProgress" small />
        {{ t('Check invitation code') }}
      </BButton>
    </template>
  </BInputGroup>
  <BFormInvalidFeedback :state="codeState">
    {{ errorMessage }}
  </BFormInvalidFeedback>
  <BFormValidFeedback :state="codeState">
    <i18n-t keypath="invitation_code_available" tag="span">
      <template #status><b>"{{ checkResult?.vip_category?.name }}"</b></template>
    </i18n-t>
  </BFormValidFeedback>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

type CheckCodeResult = {
  is_available?: boolean
  message?: string
  vip_category?: { name?: string }
  data?: CheckCodeResult
}

const props = withDefaults(
  defineProps<{
    id?: string
    buttonVariant?: string
    inputVariant?: string
    externalError?: string | null
  }>(),
  {
    buttonVariant: 'light',
    inputVariant: 'light',
    externalError: null,
  },
)

const code = defineModel<string>({ default: '' })

const emit = defineEmits<{
  checked: [valid: boolean]
}>()

const { locale, t } = useI18n({ useScope: 'local' })
const { $apiFetch } = useNuxtApp()

const generatedId = useId()
const inputId = computed(() => props.id || generatedId)
const inputVariantClass = computed(() => {
  if (!props.inputVariant || props.inputVariant === 'light') return undefined
  return `text-bg-${props.inputVariant}`
})

const checkInProgress = ref(false)
const checkResult = ref<CheckCodeResult | null>(null)
const codeErrors = ref<Record<string, string>>({})

const isValid = computed(() => checkResult.value?.is_available === true)

const errorMessage = computed(
  () => checkResult.value?.message || codeErrors.value.code || props.externalError || '',
)

const codeState = computed(() => {
  if (errorMessage.value) return false
  if (isValid.value) return true
  return null
})

// Editing the code invalidates the previous check result
watch(code, () => {
  checkResult.value = null
  codeErrors.value = {}
})

const check = async (): Promise<boolean> => {
  const trimmed = code.value.trim()
  if (!trimmed) return false

  checkInProgress.value = true
  checkResult.value = null
  codeErrors.value = {}

  let valid = false
  try {
    const res = (await $apiFetch(`/${locale.value}/api/invitations/invitations/check_code/`, {
      method: 'POST',
      body: { code: trimmed },
    })) as CheckCodeResult

    if (res.is_available === true) {
      checkResult.value = res
      valid = true
    } else {
      checkResult.value = res.data ?? res
      codeErrors.value = { code: t('Invitation code already used') }
    }
  } catch (err: unknown) {
    checkResult.value = (err as { data?: CheckCodeResult }).data ?? null
    codeErrors.value = { code: t('Invitation code not exists') }
  } finally {
    checkInProgress.value = false
  }

  emit('checked', valid)
  return valid
}

const resetErrors = () => {
  checkResult.value = null
  codeErrors.value = {}
}

defineExpose({ check, isValid, resetErrors })
</script>

<i18n lang="json">
{
  "en": {
    "Check invitation code": "Check invitation code",
    "Invitation code already used": "Invitation code already used",
    "Invitation code not exists": "Invitation code does not exist",
    "invitation_code_available": "The invitation code is available. After registration, you will be assigned the {status} status."
  },
  "ru": {
    "Check invitation code": "Проверить код",
    "Invitation code already used": "Код приглашения уже использован",
    "Invitation code not exists": "Код приглашения не существует",
    "invitation_code_available": "Код приглашения доступен. После регистрации вам будет присвоен статус {status}."
  }
}
</i18n>
